import { zodResolver } from '@hookform/resolvers/zod';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { VyIcon } from '@weft/icons';
import type { MeasurementInput } from '@weft/types';
import { measurementInputSchema } from '@weft/types';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useUploadMeasurements } from 'apps/app/src/hooks/requests/use-upload-measurements';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { UploadDialogContent } from './UploadDialogContent';

export type HandleDialogSubmitParams = {
  customerNames: string[],
  file: File
};

export const UploadMenuItem: React.FC = () => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const [isValid, setIsValid] = useState<boolean>(false);
  const uploadMeasurements = useUploadMeasurements();
  const queryClient = useQueryClient();

  const methods = useForm<MeasurementInput>({
    resolver: zodResolver(measurementInputSchema),
    defaultValues: {
      customerNames: [],
    },
  });

  const handleSubmit = methods.handleSubmit(async ({ customerNames, file }) => {
    handleClose();
    
    await runMutation(
      uploadMeasurements,
      { customerNames, file },
      {
        pending: 'Uploading...',
        success: 'Measurements uploaded',
        error: 'Failed to upload measurements',
      }, {
        onSuccess: async () => {
          await queryClient.invalidateQueries('meters');
        },
      });
  });

  const handleClick = async () => {
    const isValidFrom = await methods.trigger();

    if (isValidFrom) {
      await handleSubmit();
    }
  };

  return (
    <>
      <MenuItem onClick={handleOpen}>
        <ListItemIcon>
          <VyIcon icon="Download" />
        </ListItemIcon>
        <ListItemText>Upload Data File</ListItemText>
      </MenuItem>

      <VyDialog 
        open={isDialogOpen}
        onClose={handleClose}
        label="Upload Data File"
        cancelButtonText="Cancel"
        ctaText="Upload"
        ctaDisabled={!isValid}
        onSubmit={handleClick}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit}>
            <UploadDialogContent setValid={setIsValid}/>
          </form>
        </FormProvider> 
      </VyDialog>
    </>
  );
};